import React from 'react';
import { useJyotishaLanguage } from './JyotishaLanguageContext';
import { ExpandableSection } from '../shared/ExpandableSection';

const SHLOKA_LINES = [
  'ज्योतिषं न प्रमादाय, न च लोकवञ्चनाय च।',
  'अप्रत्यक्षाणि शास्त्राणि, विवादो नैव सम्मते॥',
];

const MEANING = {
  en: {
    summary:
      'Use jyotish to learn and guide - not to scare people, skip responsibility, or mislead them. When a shastra’s results are not directly visible, honest disagreement stays part of the picture.',
    pada1:
      'Jyotish is not for pramada (careless, rash use) or lokavancana (misleading or cheating the public).',
    pada2:
      'Apratyaksha shastras - conclusions you cannot see with your eyes - invite debate. Easy consensus is rare.',
    note: 'The second line echoes the classical subhashita on apratyaksha shastras cited in jyotisha tradition (e.g. Dharmawiki, Subhashita collections).',
  },
  hi: {
    summary:
      'ज्योतिष सीखने और समझाने के लिए है - लोगों को डराने, जिम्मेदारी छोड़ाने या धोखा देने के लिए नहीं। जिन शास्त्रों के फल सीधे दिखाई नहीं देते, वहाँ मतभेद रहना स्वाभाविक है।',
    pada1:
      'ज्योतिष प्रमाद (लापरवाही, बिना सोचे उपयोग) या लोकवञ्चन (भ्रम या धोखा) के लिए नहीं है।',
    pada2:
      'अप्रत्यक्ष शास्त्र - जिनके निष्कर्ष आँखों से नहीं देखे जाते - में विवाद चलता रहता है; सहमति आसान नहीं बनती।',
    note: 'द्वितीय पाद ज्योतिष परंपरा में उद्धृत अप्रत्यक्ष-शास्त्र वाले सुभाषित से सहमत है।',
  },
};

const POINTS = {
  en: [
    'Learn and guide - not fear, dependency, or pressure.',
    'Use with honest intent for dharma, artha, kama, and moksha.',
    'It shows karma and timing; it is not a fixed sentence.',
    'For big life calls, check with a qualified jyotishi you trust.',
  ],
  hi: [
    'सीखने और मार्गदर्शन के लिए - भय, निर्भरता या दबाव के लिए नहीं।',
    'धर्म, अर्थ, काम, मोक्ष के लिए ईमानदार भाव से उपयोग करें।',
    'कर्म और समय दिखाता है; अटल फैसला नहीं।',
    'बड़े निर्णयों पर भरोसेमंद योग्य ज्योतिषी से पुष्टि करें।',
  ],
};

function EthicsDetailBlock({ meaning, showEnglish, hi, muted, faint }) {
  const langAttr = showEnglish ? 'en' : 'hi';
  const devanagari = showEnglish ? '' : 'font-devanagari';

  return (
    <>
      <dl className={`space-y-2 text-xs leading-relaxed sm:text-sm ${muted}`}>
        <div>
          <dt className={`font-semibold ${hi} ${devanagari}`} lang={langAttr}>
            {showEnglish ? 'First line' : 'प्रथम पाद'}
          </dt>
          <dd className={`mt-0.5 ${devanagari}`} lang={langAttr}>
            {meaning.pada1}
          </dd>
        </div>
        <div>
          <dt className={`font-semibold ${hi} ${devanagari}`} lang={langAttr}>
            {showEnglish ? 'Second line' : 'द्वितीय पाद'}
          </dt>
          <dd className={`mt-0.5 ${devanagari}`} lang={langAttr}>
            {meaning.pada2}
          </dd>
        </div>
      </dl>

      <p className={`mt-2 text-[11px] italic leading-snug ${faint} ${devanagari}`} lang={langAttr}>
        {meaning.note}
      </p>
    </>
  );
}

/** Ethics shloka and meaning for the modal. */
export function JyotishaEthicsContent({ isDark }) {
  const { showEnglish } = useJyotishaLanguage();
  const panel = isDark ? 'border-amber-300/20 bg-black/25' : 'border-amber-200/90 bg-white/80';
  const hi = isDark ? 'text-amber-50' : 'text-amber-950';
  const muted = isDark ? 'text-amber-200/75' : 'text-amber-800/85';
  const faint = isDark ? 'text-amber-200/55' : 'text-amber-800/65';
  const meaning = showEnglish ? MEANING.en : MEANING.hi;
  const points = showEnglish ? POINTS.en : POINTS.hi;
  const langAttr = showEnglish ? 'en' : 'hi';
  const devanagari = showEnglish ? '' : 'font-devanagari';

  return (
    <section className={`rounded-xl border p-3.5 sm:p-4 ${panel}`}>
      <blockquote className="text-center" lang="sa">
        {SHLOKA_LINES.map((line) => (
          <p key={line} className={`font-devanagari text-sm font-semibold leading-relaxed sm:text-base ${hi}`}>
            {line}
          </p>
        ))}
      </blockquote>

      <p className={`mt-3 text-sm leading-relaxed ${hi} ${devanagari}`} lang={langAttr}>
        {meaning.summary}
      </p>

      <div className="mt-2.5 hidden sm:block">
        <EthicsDetailBlock meaning={meaning} showEnglish={showEnglish} hi={hi} muted={muted} faint={faint} />
      </div>

      <ul
        className={`mt-3 space-y-2 border-t border-current/10 pt-3 text-sm leading-snug ${hi} ${devanagari}`}
        lang={langAttr}
      >
        {points.map((point) => (
          <li key={point} className="flex gap-2">
            <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${isDark ? 'bg-amber-300' : 'bg-amber-600'}`} aria-hidden />
            <span>{point}</span>
          </li>
        ))}
      </ul>

      <div className="mt-3 sm:hidden">
        <ExpandableSection
          showChevron={false}
          summaryClassName={`text-xs font-semibold leading-snug ${hi} ${devanagari} ${muted}`}
          contentClassName="mt-2"
          summary={
            <span lang={langAttr}>
              <span className="underline decoration-amber-400/50 underline-offset-2">
                {showEnglish ? 'Line-by-line meaning' : 'पाद-वार अर्थ'}
              </span>
              <span className="ml-1 opacity-60" aria-hidden>
                ▾
              </span>
            </span>
          }
        >
          <EthicsDetailBlock meaning={meaning} showEnglish={showEnglish} hi={hi} muted={muted} faint={faint} />
        </ExpandableSection>
      </div>
    </section>
  );
}
